<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useSnapCarousel } from '~/features/my-wallet/composables/useSnapCarousel'

interface Props {
  itemCount: number
  startIndex: number
  label: string
  /** Essential profile: one card and the banner (1fr 2fr from 1024 px) instead of the three-card grid. */
  essential?: boolean
}

const props = defineProps<Props>()

defineSlots<{
  default: (scope: { index: number }) => unknown
}>()

const { t } = useI18n()

const track = useTemplateRef<HTMLElement>('track')
const { currentIndex, goTo, handleScroll } = useSnapCarousel(track, () => props.startIndex)
</script>

<template>
  <div class="card-carousel" :class="{ '-essential': essential }">
    <ul ref="track" class="card-carousel-track" :aria-label="label" @scroll.passive="handleScroll">
      <li v-for="index in itemCount" :key="index" class="card-carousel-item">
        <slot :index="index - 1" />
      </li>
    </ul>
    <div v-if="itemCount > 1" class="card-carousel-dots">
      <button
        v-for="position in itemCount"
        :key="position"
        type="button"
        class="card-carousel-dot"
        :class="{ '-current': position - 1 === currentIndex }"
        :aria-label="t('my_wallet.receiving_model.carousel.go_to', { index: position, total: itemCount })"
        :aria-current="position - 1 === currentIndex ? 'true' : undefined"
        @click="goTo(position - 1)"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.card-carousel {
  @apply flex flex-col gap-3;
  container-type: inline-size;

  .card-carousel-track {
    @apply relative flex gap-4 overflow-x-auto;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    margin: 0 -1rem;
    padding: 0 calc(7.5% + 1rem);
    padding: 0 calc(7.5cqw + 1rem);

    &::-webkit-scrollbar {
      @apply hidden;
    }

    @screen sm:system {
      margin: 0 -1.5rem;
      padding: 0 calc(22.5% + 1.5rem);
      padding: 0 calc(22.5cqw + 1.5rem);
    }

    @screen md:system {
      @apply grid grid-cols-3 m-0 p-0 overflow-visible;
    }
  }

  &.-essential {
    .card-carousel-track {
      @screen sm:system {
        padding: 0 calc(11% + 1.5rem);
        padding: 0 calc(11cqw + 1.5rem);
      }

      @screen md:system {
        @apply p-0;
        grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
      }
    }

    .card-carousel-item {
      @screen sm:system {
        width: 78vw;
        width: 78cqw;
      }

      @screen md:system {
        @apply w-auto;
      }
    }
  }

  .card-carousel-item {
    @apply flex flex-shrink-0;
    width: 72vw;
    width: 85cqw;
    scroll-snap-align: center;

    @screen sm:system {
      width: 50vw;
      width: 55cqw;
    }

    @screen md:system {
      @apply w-auto min-w-0;
    }
  }

  .card-carousel-dots {
    @apply flex justify-center gap-2;

    @screen md:system {
      @apply hidden;
    }
  }

  .card-carousel-dot {
    @apply relative h-2 w-2 rounded-full bg-neutral-high-medium cursor-pointer;
    transition: width 0.2s ease;

    &::before {
      @apply absolute;
      content: '';
      inset: -0.5rem;
    }

    &.-current {
      @apply w-6 bg-neutral-low-dark;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
}
</style>
